import { useLocation } from "react-router-dom";
import SEO from "./SEO";
import SEOConfig, { publisherRef } from "./SEOConfig";
import { SOCIAL_LINKS } from "../../constants/social";

// Races are broadcast on the YouTube channel, which is the closest thing an online race has to a
// venue.
const RACE_BROADCAST_URL = `${SOCIAL_LINKS.youtube}/streams`;

// One SportsEvent per race of the selected season. Only races with a date are emitted:
// `startDate` is required, and a guessed one would be worse than none.
const raceEvents = (season, pageUrl) =>
  (season?.races || [])
    .filter((race) => race.date)
    .map((race) => ({
      "@type": "SportsEvent",
      name: `${season.shortName} — ${race.shortName || race.name}`,
      startDate: race.date,
      description: [race.name, race.configuration, race.car].filter(Boolean).join(" · "),
      url: pageUrl,
      sport: "Sim racing",
      eventStatus: "https://schema.org/EventScheduled",
      eventAttendanceMode: "https://schema.org/OnlineEventAttendanceMode",
      location: {
        "@type": "VirtualLocation",
        url: RACE_BROADCAST_URL,
      },
      ...(race.image && { image: race.image }),
      organizer: publisherRef,
    }));

const ResultsSEO = ({ season }) => {
  const location = useLocation();
  const { siteUrl } = SEOConfig;
  const fullUrl = `${siteUrl}${location.pathname}`;

  const title = "Результати гонок і турнірна таблиця";
  const description =
    "Результати формульної ліги симрейсингу Slick Racing Ukraine в iRacing: спринти, гонки й кваліфікації, очки та турнірна таблиця сезону.";

  // Add relevant keywords for the results page
  const keywords = [
    "Slick Racing Ukraine результати",
    "симрейсинг результати",
    "сімрейсінг результати",
    "віртуальний автоспорт статистика",
    "iRacing результати гонок",
    "турнірна таблиця",
    "чемпіонат результати",
    "гоночна статистика",
  ];

  // Define breadcrumbs for better navigation and SEO
  const breadcrumbs = [
    {
      name: "Головна",
      url: siteUrl,
    },
    {
      name: "Результати",
      url: fullUrl,
    },
  ];

  const events = raceEvents(season, fullUrl);

  // Until a season has loaded there are no races to describe, so the page is just a collection.
  const schema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: title,
    description,
    url: fullUrl,
    inLanguage: "uk",
    publisher: publisherRef,
  };
  if (events.length > 0) {
    schema.mainEntity = {
      "@type": "ItemList",
      name: season.shortName,
      numberOfItems: events.length,
      itemListElement: events.map((event, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: event,
      })),
    };
  }

  return (
    <SEO
      title={title}
      description={description}
      keywords={keywords}
      url={fullUrl}
      schema={schema}
      canonical={fullUrl}
      type="website"
      breadcrumbs={breadcrumbs}
      lang="uk"
    />
  );
};

export default ResultsSEO;
